import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import {
  SubmissionStatusId,
  type SubmissionDetail,
  type SubmissionValidationResult,
  type ValidationIssueRow,
} from '@/core/models/filing.models';
import { notifyError, notifySuccess } from '@/core/notify/notify';
import { filingService } from '@/core/services/filing';
import { outputProfileLabel } from '@/core/outputProfile';
import { EMAIL_PATTERN } from '@/features/manage/formRules';
import { Button } from '@/kit/Button';
import { DataTable } from '@/kit/DataTable';
import { InputField } from '@/kit/FormField';
import { Icon } from '@/kit/Icon';
import { Step, Stepper, type StepperHandle } from '@/kit/Stepper';
import { Tooltip } from '@/kit/Tooltip';
import { SideMenuPage } from '@/features/taxreturn/menus/SideMenuPage';
import { useBusy, useClientGuard } from './shared';
import './TaxreturnTaskSubmitList.scss';

/** Companies House issues a 6-character code; while filing is sandboxed (nothing is sent) 6 to 15, as Capium 3.0's test mode took. */
const authCodePattern = /^[A-Za-z0-9]{6}$/;
const sandboxAuthCodePattern = /^[A-Za-z0-9]{6,15}$/;

const LAST_STEP_INDEX = 3;

/** The tooltip text of the template, newlines and indentation included. */
const INDENT = ' '.repeat(36);
export const GATEWAY_TOOLTIP = [
  'To submit your accounts online you should have following details:',
  '',
  `${INDENT}User Name/Presenter ID : Presenter ID is basically a login id which is issued by the Companies House to file company accounts online (Please NOTE, this will not be your usual Companies House login details).`,
  INDENT,
  `${INDENT}Password/Auth. Code : Authentication code is also issued by the Companies House with your Presenter ID.`,
  INDENT,
  `${INDENT}Email: Email address where you wish to receive submission related confirmation sent by the Companies House.`,
  INDENT,
  `${INDENT}Company Authorization Code : It specifies the company specific 6 digits alphanumeric code issued by the Companies House.`,
  INDENT,
  `${INDENT}Please click here to know more about registration and get presenter details.`,
].join('\n');

interface Gateway {
  presenterId: string;
  presenterAuthentication: string;
  email: string;
  companyAuthorisationCode: string;
}

/** Whether a blocking row is the "finalise the accounts" one (the message is the API's, either wording). */
const isFinaliseIssue = (row: ValidationIssueRow) => /finalis/i.test(row.warning);

/** The accounts type a Finalise row's Resolve link opens on (`&profile=ABRIDGED`); Full when the link names none. */
const profileOfRoute = (route: string | null) => outputProfileLabel(/[?&]profile=(\w+)/i.exec(route ?? '')?.[1]);

/**
 * Shown above the checks while Next is locked: names each red item, and spells out how to clear the common one (the
 * accounts are not finalised yet), since the red rows only say what is missing.
 */
function BlockedGuide({
  issues,
  onResolve,
}: {
  issues: ValidationIssueRow[];
  onResolve: (issue: ValidationIssueRow) => void;
}) {
  const finalise = issues.find((row) => isFinaliseIssue(row) && row.route) ?? issues.find(isFinaliseIssue);
  const accounts = profileOfRoute(finalise?.route ?? null);
  return (
    <div className="submit-blocked-guide mt-2 w-760" role="alert">
      <p className="fw-bold m-0">
        Next is locked until the {issues.length === 1 ? 'red item is' : 'red items are'} fixed.
      </p>
      {finalise && (
        <>
          <p className="mt-1 mb-0">
            This submission files the <strong>{accounts}</strong>. To finalise them:
          </p>
          <ol>
            <li>
              Open Approval &amp; Signatories
              {finalise.route && (
                <>
                  {' '}
                  (
                  <a className="link" onClick={() => onResolve(finalise)}>
                    go there now
                  </a>
                  )
                </>
              )}
              : it opens on this accounting period and these accounts.
            </li>
            <li>
              Check the accounts-type box (left of the version box) says <strong>{accounts}</strong>. Accounts finalised
              under a different type do not count for this submission.
            </li>
            <li>
              Click Generate accounts version, then Approve this version. (If the accounting period is not approved yet,
              first move it on with Start preparation, Mark ready for review, Start review and Approve accounts.)
            </li>
            <li>
              Click Finalise accounts. It is offered for these accounts even when another type is already finalised.
            </li>
            <li>Come back to Submit: the checks run again, and Next opens once the red items are gone.</li>
          </ol>
        </>
      )}
      {issues.some((row) => !isFinaliseIssue(row)) && (
        <p className="mt-1 mb-0">
          Fix the other red rows in the table below; each one's Resolve link opens the screen that fixes it.
        </p>
      )}
    </div>
  );
}

/** The shown-as-dots stand-in for the practice presenter's authentication code, which the browser never has. */
export const SAVED_CODE_MASK = '••••••••••••';

const sameId = (a: string | null | undefined, b: string | null | undefined) =>
  !!a && !!b && a.trim().toLowerCase() === b.trim().toLowerCase();

/**
 * The first failing message of the gateway details, or null when they can be sent. `savedCode` is true while the
 * practice presenter's ID is filled in with its masked code: the server holds the code, so none has to be typed.
 */
export function gatewayError(gateway: Gateway, savedCode = false, sandbox = false): string | null {
  // cap3-g: both presenter fields blank files as the practice presenter configured on the filing service.
  const presenterId = gateway.presenterId.trim();
  const presenterAuthentication = gateway.presenterAuthentication.trim();
  if (!presenterId && presenterAuthentication) {
    return 'Enter the presenter ID, or clear the authentication code to use the practice presenter.';
  }
  if (presenterId && !presenterAuthentication && !savedCode) {
    return 'Enter the presenter authentication code, or clear the presenter ID to use the practice presenter.';
  }
  if (!(sandbox ? sandboxAuthCodePattern : authCodePattern).test(gateway.companyAuthorisationCode.trim())) {
    return sandbox
      ? 'The company authentication code is 6 to 15 letters or digits.'
      : 'The company authentication code is 6 letters or digits.';
  }
  const email = gateway.email.trim();
  if (email && !EMAIL_PATTERN.test(email)) {
    return 'Enter a valid email address.';
  }
  return null;
}

/**
 * Submission wizard: validate, verify, gateway details, submit. Submissions go through the AP filing service, which
 * currently runs its Companies House sandbox gateway; the resulting status says so.
 */
export function TaxreturnTaskSubmitList() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const client = useClientGuard();
  const submitting = useBusy();
  const stepper = useRef<StepperHandle>(null);

  const [submissionId] = useState(() => searchParams.get('id'));
  const [dataSource, setDataSource] = useState<ValidationIssueRow[]>([]);
  const [detail, setDetail] = useState<SubmissionDetail | null>(null);
  const [validation, setValidation] = useState<SubmissionValidationResult | null>(null);
  const [selectedIndex, setSelectedIndex] = useState(0);
  /** The code field was typed in (even cleared): from then on it is what it shows, not the masked saved code. */
  const [codeTouched, setCodeTouched] = useState(false);
  const [gateway, setGateway] = useState<Gateway>({
    presenterId: '',
    presenterAuthentication: '',
    email: '',
    companyAuthorisationCode: '',
  });

  const close = () => void navigate('/home/taxreturns/tasks-submit');

  // The practice presenter's ID is filled in, so its code (held on the server) is too, shown masked until typed over.
  const savedCode =
    !codeTouched && !gateway.presenterAuthentication && sameId(gateway.presenterId, detail?.practicePresenterId);

  const loadDetail = (id: string, prefill: boolean) => {
    filingService.getSubmission(id).then(
      (result) => {
        setDetail(result);
        if (prefill) {
          const lastAttempt = result.attempts[0];
          setGateway((current) => ({
            ...current,
            companyAuthorisationCode: result.companyAuthorisationCode ?? '',
            // Capium 3.0 filled the saved presenter in: the last one used, else the practice's own.
            presenterId: lastAttempt?.presenterId || (result.practicePresenterId ?? ''),
            email: lastAttempt?.email ?? '',
          }));
        }
      },
      (error) => {
        notifyError(error);
        close();
      },
    );
  };

  const runValidation = (id: string) => {
    filingService.validateSubmission(id).then(
      (result) => {
        setValidation(result);
        setDataSource(result.issues);
      },
      (error) => notifyError(error),
    );
  };

  /** Validates again: until the answer is back, `validation` is null (Next says the checks are still running). */
  const validate = (id: string) => {
    setValidation(null);
    runValidation(id);
  };

  useEffect(() => {
    if (!client) {
      return;
    }
    if (!submissionId) {
      notifyError('Choose a submission from the Submit list first.');
      void navigate('/home/taxreturns/tasks-submit');
      return;
    }
    loadDetail(submissionId, true);
    // Nothing has been validated yet, so `validation` is already null.
    runValidation(submissionId);
    // Runs once, when the screen opens (ngOnInit).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [client]);

  const resolve = (issue: ValidationIssueRow) => {
    if (issue.route) {
      void navigate(issue.route);
    }
  };

  const submit = () => {
    if (!submissionId || submitting.isBusy) {
      return;
    }
    const error = gatewayError(gateway, savedCode, !!detail?.isSandbox);
    if (error) {
      notifyError(error);
      return;
    }
    if (!submitting.begin()) {
      return;
    }

    filingService
      .submitSubmission(submissionId, {
        presenterId: gateway.presenterId.trim(),
        presenterAuthentication: gateway.presenterAuthentication.trim(),
        email: gateway.email.trim() || null,
        companyAuthorisationCode: gateway.companyAuthorisationCode.trim().toUpperCase(),
      })
      .then(
        (result) => {
          setDetail(result);
          setGateway((current) => ({ ...current, presenterAuthentication: '' }));
          setCodeTouched(false);
          const attempt = result.attempts[0];
          const messages = (attempt?.messages ?? []).map((m) => m.message).join(' ');
          if (result.statusId === SubmissionStatusId.Pending || result.statusId === SubmissionStatusId.Accepted) {
            notifySuccess(`${result.refNo}: ${result.status}. ${messages}`.trim());
            close();
          } else {
            notifyError(`${result.refNo}: ${result.status}. ${messages}`.trim());
            validate(submissionId);
          }
        },
        (err) => {
          notifyError(err);
          loadDetail(submissionId, false);
          validate(submissionId);
        },
      )
      .finally(submitting.end);
  };

  const onNextClick = () => {
    const handle = stepper.current;
    if (!handle) {
      return;
    }
    switch (handle.selectedIndex) {
      case 0:
        if (!validation) {
          notifyError('The validation checks are still running.');
          return;
        }
        if (validation.blockingCount > 0) {
          notifyError('Resolve the blocking validation issues before continuing.');
          return;
        }
        handle.next();
        return;
      case 2: {
        const error = gatewayError(gateway, savedCode, !!detail?.isSandbox);
        if (error) {
          notifyError(error);
          return;
        }
        handle.next();
        return;
      }
      case LAST_STEP_INDEX:
        submit();
        return;
      default:
        handle.next();
    }
  };

  const setField = (field: keyof Gateway) => (event: { target: { value: string } }) =>
    setGateway((current) => ({ ...current, [field]: event.target.value }));

  return (
    <SideMenuPage menu="task">
      <div>
        <div className="sidenav-details mb-4">
          <div className="sidenav-header">
            <div className="d-flex align-items-center justify-space-between">
              <h2>Submit {detail?.refNo}</h2>
              <Button className="close-btn" aria-label="Close" onClick={close}>
                <Icon name="close" />
              </Button>
            </div>
          </div>
          <div className="import-stepper client-import">
            <div className="d-flex flex-column mt-2">
              <Stepper
                ref={stepper}
                linear={false}
                onSelectionChange={(event) => setSelectedIndex(event.selectedIndex)}
              >
                <Step label="Validate Report">
                  {validation && validation.blockingCount > 0 && (
                    <BlockedGuide issues={dataSource.filter((row) => row.isBlocking)} onResolve={resolve} />
                  )}
                  <div className="mat-table-wrapper mt-2 w-760">
                    <DataTable
                      stickyHeader
                      rows={dataSource}
                      rowKey={(row, index) => `${row.srNo}-${index}`}
                      columns={[
                        { id: 'srno', header: 'Sr. No', cell: (row) => row.srNo },
                        {
                          id: 'warning',
                          header: 'Warning',
                          cellClassName: (row) => (row.isBlocking ? 'text-red' : undefined),
                          cell: (row) => row.warning,
                        },
                        {
                          id: 'link',
                          header: 'Link',
                          cell: (row) => (
                            <a className="link" onClick={() => resolve(row)}>
                              {row.link}
                            </a>
                          ),
                        },
                      ]}
                    />
                  </div>
                </Step>
                <Step label="Verify Report">
                  <div className="report-img mt-2">
                    <img src="assets/images/report-img.jpg" alt="Verify Report" />
                  </div>
                </Step>
                <Step label="Gateway Details">
                  <div className="d-flex justify-space-between mt-2 w-760">
                    <div>
                      <div className="form-field">
                        <p>User Name/Presenter ID</p>
                        <InputField
                          fieldClassName="w-387"
                          autoComplete="off"
                          maxLength={255}
                          charMode="alphanumeric"
                          name="presenterId"
                          value={gateway.presenterId}
                          onChange={setField('presenterId')}
                        />
                      </div>
                      <div className="form-field">
                        <p>Password/Auth Code</p>
                        <InputField
                          fieldClassName="w-387"
                          // Chrome ignores "off" on password fields and fills in the user's saved Capium login here;
                          // this is the Companies House presenter authentication code, never that password.
                          autoComplete="new-password"
                          maxLength={255}
                          name="presenterAuthentication"
                          type="password"
                          value={savedCode ? SAVED_CODE_MASK : gateway.presenterAuthentication}
                          onFocus={(event) => {
                            if (savedCode) event.currentTarget.select();
                          }}
                          onChange={(event) => {
                            setCodeTouched(true);
                            setField('presenterAuthentication')(event);
                          }}
                        />
                        <div className="text-gray font-size-12 mt-5">Enter both or leave both empty.</div>
                      </div>
                      <div className="form-field">
                        <p>Email</p>
                        <InputField
                          fieldClassName="w-387"
                          autoComplete="off"
                          maxLength={255}
                          name="email"
                          value={gateway.email}
                          onChange={setField('email')}
                        />
                      </div>
                      <div className="form-field">
                        <p>
                          Company Authorisation Code<span className="text-red">*</span>
                        </p>
                        <InputField
                          fieldClassName="w-387"
                          autoComplete="off"
                          maxLength={255}
                          charMode="alphanumeric"
                          name="companyAuthorisationCode"
                          value={gateway.companyAuthorisationCode}
                          onChange={setField('companyAuthorisationCode')}
                        />
                      </div>
                    </div>
                    <div>
                      <Tooltip title={GATEWAY_TOOLTIP}>
                        <Icon name="emoji_objects" className="text-yellow-200 highlight-icon" />
                      </Tooltip>
                    </div>
                  </div>
                </Step>
                <Step label="Submit Accounts">
                  <div className="w-760">
                    <div className="sub-wrap">
                      <div className="sub-acc">
                        <div className="sub-ttl">Company Name :</div>
                        <div className="sub-dtl">{detail?.companyName || '-'}</div>
                      </div>
                      <div className="sub-acc">
                        <div className="sub-ttl">Company Number :</div>
                        <div className="sub-dtl">{detail?.companyNumber || '-'}</div>
                      </div>
                      <div className="sub-acc">
                        <div className="sub-ttl">Location :</div>
                        <div className="sub-dtl">{detail?.location || '-'}</div>
                      </div>
                    </div>
                    <div className="sub-wrap">
                      <div className="sub-acc">
                        <div className="sub-ttl">Report Name :</div>
                        <div className="sub-dtl">{detail?.reportName || '-'}</div>
                      </div>
                      <div className="sub-acc">
                        <div className="sub-ttl">Period Covered :</div>
                        <div className="sub-dtl">{detail?.periodCovered || '-'}</div>
                      </div>
                      <div className="sub-acc">
                        <div className="sub-ttl">Director Sign Date :</div>
                        <div className="sub-dtl">{detail?.directorSignDate || '-'}</div>
                      </div>
                      <div className="sub-acc">
                        <div className="sub-ttl">Accountant Sign Date :</div>
                        <div className="sub-dtl">{detail?.accountantSignDate || '-'}</div>
                      </div>
                    </div>
                    <div className="sub-wrap">
                      <div className="sub-acc">
                        <div className="sub-ttl">Company Auth Code :</div>
                        <div className="sub-dtl">{gateway.companyAuthorisationCode || '-'}</div>
                      </div>
                      <div className="sub-acc">
                        <div className="sub-ttl">User ID :</div>
                        <div className="sub-dtl">{gateway.presenterId || '-'}</div>
                      </div>
                      <div className="sub-acc">
                        <div className="sub-ttl">Email :</div>
                        <div className="sub-dtl">{gateway.email || '-'}</div>
                      </div>
                    </div>
                  </div>
                </Step>
              </Stepper>
            </div>
          </div>
        </div>
        <div className="action-wrapper">
          <div className="content-body">
            <Button
              className="action-button secondary-button mr-1"
              type="button"
              disabled={selectedIndex === 0 || submitting.isBusy}
              onClick={() => stepper.current?.previous()}
            >
              Previous
            </Button>
            <Button
              className="action-button primary-button"
              type="button"
              disabled={submitting.isBusy}
              onClick={onNextClick}
            >
              {selectedIndex === LAST_STEP_INDEX ? 'Submit' : 'Next'}
            </Button>
          </div>
        </div>
      </div>
    </SideMenuPage>
  );
}
